import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface SearchableSelectOption {
    value: string;
    label: string;
    description?: string;
}

interface SearchableSelectProps {
    options: SearchableSelectOption[];
    value?: string;
    onValueChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    emptyMessage?: string;
    searchPlaceholder?: string;
    /** When provided, search filtering is delegated to the caller instead of done locally. */
    onSearch?: (search: string) => void;
    loading?: boolean;
}

export const SearchableSelect = React.forwardRef<HTMLButtonElement, SearchableSelectProps>(
    (
        {
            options,
            value,
            onValueChange,
            placeholder = 'Select option...',
            disabled = false,
            className,
            emptyMessage = 'No option found.',
            searchPlaceholder = 'Search...',
            onSearch,
            loading = false,
        },
        ref
    ) => {
        const [open, setOpen] = React.useState(false);
        const [search, setSearch] = React.useState('');

        const selectedOption = options.find(option => option.value === value);

        const handleSearchChange = (value: string) => {
            setSearch(value);
            onSearch?.(value);
        };

        const filteredOptions = React.useMemo(() => {
            if (onSearch) {
                // Server-side search — caller filters options
                return options;
            }
            const normalizedSearch = search.trim().toLowerCase();
            if (!normalizedSearch) {
                return options;
            }

            return options.filter(option => {
                const searchableValues = [option.label, option.description, option.value].filter(
                    Boolean
                ) as string[];

                return searchableValues.some(item => item.toLowerCase().includes(normalizedSearch));
            });
        }, [options, search, onSearch]);

        React.useEffect(() => {
            if (!open) {
                setSearch('');
            }
        }, [open]);

        return (
            <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                    <Button
                        ref={ref}
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            'w-full justify-between font-normal',
                            !selectedOption && 'text-muted-foreground',
                            className
                        )}
                        disabled={disabled}
                    >
                        <span className="truncate">
                            {selectedOption ? selectedOption.label : placeholder}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    onOpenAutoFocus={event => event.preventDefault()}
                    onCloseAutoFocus={event => event.preventDefault()}
                >
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={searchPlaceholder}
                            className="h-9"
                            value={search}
                            onValueChange={handleSearchChange}
                        />
                        <CommandList>
                            <CommandEmpty>{loading ? 'Loading...' : emptyMessage}</CommandEmpty>
                            <CommandGroup>
                                {filteredOptions.map(option => (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onMouseDown={event => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        onSelect={() => {
                                            onValueChange?.(option.value);
                                            setOpen(false);
                                            setSearch('');
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                'mr-2 h-4 w-4',
                                                value === option.value ? 'opacity-100' : 'opacity-0'
                                            )}
                                        />
                                        <div className="flex flex-col">
                                            <span>{option.label}</span>
                                            {option.description && (
                                                <span className="text-xs text-muted-foreground">
                                                    {option.description}
                                                </span>
                                            )}
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        );
    }
);

SearchableSelect.displayName = 'SearchableSelect';
